import Phaser from 'phaser';
import { SCENE_KEYS, ZONE_NAMES } from '../data/gameConstants';
import { Collectible } from '../objects/Collectible';
import { InteractionZone } from '../objects/InteractionZone';
import { SaveSystem } from '../systems/SaveSystem';
import { ZoneCoreSystems } from '../systems/ZoneCoreSystems';
import { ObjectiveHint } from '../ui/ObjectiveHint';
import { QuestPanel } from '../ui/QuestPanel';
import { addZoneHudHeader } from '../ui/ZoneHudHeader';
import { addZoneMenuButton } from '../ui/zoneMenuButton';
import { Zone2AnimalEvents } from '../zones/zone2/Zone2AnimalEvents';
import { SceneTransition } from '../utils/SceneTransition';
import { ZONE2_POSITIONS, Zone2Map } from '../zones/zone2/Zone2Map';
import { Zone2QuestFlow } from '../zones/zone2/Zone2QuestFlow';

export class Zone2ForestSurvivalScene extends Phaser.Scene {
  private core?: ZoneCoreSystems;
  private questPanel?: QuestPanel;
  private questFlow?: Zone2QuestFlow;
  private map?: Zone2Map;
  private keris?: Collectible;
  private objectiveHint?: ObjectiveHint;

  constructor() {
    super({ key: 'Zone2ForestSurvivalScene' });
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    this.map = new Zone2Map(this);
    const obstacles = this.map.build();

    this.core = new ZoneCoreSystems(this);
    this.questPanel = new QuestPanel(this);
    this.objectiveHint = new ObjectiveHint(this);
    this.core.setQuestPanel(this.questPanel);
    this.core.setupHUD(2);
    this.core.setupMobileControls();
    const startPos = SaveSystem.getPlayerStartPosition(
      SCENE_KEYS.ZONE2,
      ZONE2_POSITIONS.SPAWN.x,
      ZONE2_POSITIONS.SPAWN.y,
    );
    SaveSystem.saveProgress(SCENE_KEYS.ZONE2, 2, startPos);
    this.core.initPlayer(startPos.x, startPos.y);
    this.core.setupObstacles(obstacles);

    this.keris = new Collectible(this, ZONE2_POSITIONS.KERIS.x, ZONE2_POSITIONS.KERIS.y, {
      id: 'zone2_keris1',
      label: 'Keris 1',
      type: 'keris_1',
      scoreRuleId: 'zone2_keris1',
      dialogueId: 'zone2_rafflesia',
      kerisId: 'keris_1',
      startHidden: true,
      interactionRadius: 100,
    });
    const animalZone = new InteractionZone(
      this,
      ZONE2_POSITIONS.ANIMAL_TRIGGER.x,
      ZONE2_POSITIONS.ANIMAL_TRIGGER.y,
      120,
      90,
      {
        id: 'zone2_animal_sign',
        type: 'custom',
        label: 'Amati Tanda Alam',
        interactionRadius: 130,
      },
    );

    const kerisReadZone = new InteractionZone(
      this,
      ZONE2_POSITIONS.KERIS.x,
      ZONE2_POSITIONS.KERIS.y,
      110,
      90,
      {
        id: 'zone2_keris_read',
        type: 'custom',
        label: 'Baca Petunjuk Keris 1',
        interactionRadius: 120,
      },
    );
    kerisReadZone.setEnabled(false);

    const exitZone = new InteractionZone(
      this,
      ZONE2_POSITIONS.EXIT.x - 40,
      ZONE2_POSITIONS.EXIT.y,
      90,
      100,
      {
        id: 'zone2_exit',
        type: 'custom',
        label: 'Ke Rumah Bubungan',
        interactionRadius: 110,
      },
    );
    exitZone.setEnabled(false);

    const animals = new Zone2AnimalEvents(this);

    this.questFlow = new Zone2QuestFlow(
      this.core,
      this.map,
      this.keris,
      this.objectiveHint,
      animals,
    );

    this.keris.setOnCollect(() => this.questFlow!.onKerisCollected());
    animalZone.setOnInteract(() => this.questFlow!.onAnimalSignInteract());
    kerisReadZone.setOnInteract(() => this.questFlow!.onKerisReadInteract());
    exitZone.setOnInteract(() => this.questFlow!.onExitInteract());
    this.questFlow.setInteractionZones({
      animal: animalZone,
      kerisRead: kerisReadZone,
      exit: exitZone,
    });
    this.core.registerInteractables([
      animalZone,
      kerisReadZone,
      exitZone,
      this.keris,
    ]);
    this.buildUI();
    this.questFlow.start();
    this.core.refreshQuestPanel(2);
    SceneTransition.reveal(this);
  }

  private buildUI(): void {
    addZoneHudHeader(this, ZONE_NAMES.ZONE2, 'Tanda alam, Keris Pusaka, & simulasi gempa');
    addZoneMenuButton(this);
  }

  update(time: number, delta: number): void {
    this.core?.update(time, delta);
    this.keris?.updateGlow();

    const player = this.core?.getPlayer();
    if (player && this.questFlow) {
      this.questFlow.update(delta, player.x, player.y);
    }
  }

  shutdown(): void {
    this.questFlow?.destroy();
    this.core?.destroy();
    this.questFlow = undefined;
    this.core = undefined;
    this.questPanel = undefined;
  }
}
